'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatTime } from '@fe-common-utils/libs/time';
import { GETv2Search } from '@fe-news/api/news';
import { NewsListItemType } from '@fe-news/constants/news/type';
import CardSize from '@fe-news/constants/size/card';
import { useInfiniteQuery } from '@tanstack/react-query';

const PRE_PAGE_COUNT = 20;
const DEFAULT_PAGE = 1;
const MILLISECONDS = 1000;
const SECONDS = 60;
const ONE_MINUTES = MILLISECONDS * SECONDS * 1;

interface ReduceAccumulator {
  dates: Set<string>;
  items: (News | LabelType)[];
}

const getTagNews = async ({
  pageParam = 1,
  queryKey,
  dates = [],
  title
}: {
  pageParam?: number;
  queryKey?: string[];
  dates?: number[];
  title?: string;
}): Promise<PageData<News[]>> => {
  const [, tag] = queryKey || [];
  const [startAt = '', endAt = ''] = dates;

  try {
    const response = await GETv2Search({
      q: tag,
      page: pageParam,
      startAt,
      endAt,
      title
    });

    return {
      data: response?.items?.data || [],
      total: response?.items?.total || 0,
      currentPage: response?.items?.current_page || DEFAULT_PAGE,
      lastPage: response?.items?.last_page || DEFAULT_PAGE,
      perPage: response?.items?.per_page || PRE_PAGE_COUNT,
      from: response?.items?.from || DEFAULT_PAGE,
      to: response?.items?.to || DEFAULT_PAGE
    };
  } catch (error) {
    console.error('[API Error]: GETv2Search', error);
    return {
      data: [],
      total: 0,
      currentPage: DEFAULT_PAGE,
      lastPage: DEFAULT_PAGE,
      perPage: PRE_PAGE_COUNT,
      from: DEFAULT_PAGE,
      to: DEFAULT_PAGE
    };
  }
};

const todayDate = formatTime(Date.now() / 1000, 'YYYYMMDD');

const formateData = (data: News[], isLastPage = false) => {
  if (!data?.length) return [];

  const defaultData = {
    dates: new Set() as Set<string>,
    items: [] as (News | LabelType)[]
  };

  data.sort((a, b) => b.publishAt - a.publishAt);

  return (
    data.reduce<ReduceAccumulator>((accumulator, item, index) => {
      const itemDate = formatTime(item.publishAt, 'YYYYMMDD');
      if (!accumulator.dates.has(itemDate) && itemDate !== todayDate) {
        accumulator.dates.add(itemDate);

        if (!isLastPage && index !== 0) {
          accumulator.items.push({
            type: NewsListItemType.DATE,
            publishAt: item.publishAt,
            height: CardSize.DesktopDate
          });
        }
      }
      accumulator.items.push(item);
      return accumulator;
    }, defaultData)?.items ?? []
  );
};

type FetchInfiniteTagProps = {
  tag: string;
  keyword?: MaybeUndefined<string>;
  page?: MaybeUndefined<number>;
  dates?: MaybeUndefined<number[]>;
  title?: MaybeUndefined<string>;
};

const useInfiniteFetchTag = ({ tag, page = DEFAULT_PAGE, dates = [], title }: FetchInfiniteTagProps) => {
  const nextPage = page + DEFAULT_PAGE;
  const [startAt, endAt] = dates ?? [];
  const startAtDate = new Date(startAt).getTime();
  const endAtDate = new Date(endAt).getTime();
  const queryKey = [
    'news',
    tag,
    `startAtDate:${Math.floor(startAtDate / ONE_MINUTES)}`,
    `endAtDate:${Math.floor(endAtDate / ONE_MINUTES)}`,
    `title:${title}`
  ];

  const {
    data: queryData,
    hasNextPage,
    fetchNextPage,
    isFetching
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = nextPage }: any) => getTagNews({ queryKey, pageParam, dates, title }),
    getNextPageParam: (lastPage, pages) => pages.length + 1,
    initialPageParam: 1
  });

  const data = (queryData?.pages?.map(page => page.data || []).flat() || []) as ListNewsItem[];
  const total = queryData?.pages[0]?.total;
  const currentPage = queryData?.pages[0]?.currentPage;
  const lastPage = queryData?.pages[0]?.lastPage;

  const isLastPage = currentPage === lastPage;

  const isItemLoaded = (index: number) => index < data.length && data[index] !== null;

  const loadMoreItems = () => {
    hasNextPage && fetchNextPage();
  };

  return { data: formateData(data, isLastPage), isItemLoaded, loadMoreItems, isFetching, total };
};

export default useInfiniteFetchTag;
